import { useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import NoiseJS from 'noisejs';
import { Poline, positionFunctions } from 'poline';
import useKeypress from 'react-use-keypress';
import { THEMES } from '@thi.ng/color-palettes';
import { RND, weightedKey } from '@thi.ng/random-fxhash';

import { useMaterial } from './useMaterial';
import { registerFeatures } from './fxhash';

console.log(
  'A Great Space Mind Unfolds Before You is an evocative long-form generative art piece with motion.'
);
console.log('Space bar toggles pause/play.');
console.log('Click/drag orbit and scroll to zoom.');

function logColor(c) {
  console.log(`Color %c ${c} `, `background: ${c}; color: #000`);
}

var noise = new NoiseJS.Noise(RND.float());

const zooms = { Standard: 3, Far: 1 };
const characters = { Friendly: 4, Foe: 1, Shifting: 1 };
const themes = {
  '00RnZfzWN7Ewml76I': 1,
  '00bYahDYU6E7wDJwL': 1,
  '00QLNIKFhjs5rJCjr': 1,
  '00RB4IFxoIYII3Cqi': 1,
  '014wmwCkvNXm2qCVW': 1,
  '014cMCdTh3bwH8wVB': 1,
  '00bYcjDCylnf3k1aB': 1,
  '014xtY7NR86Aa2AMH': 1,
  '014c8uRpFlkiHSo9k': 1,
  '00bYcj4uvi0NIlqyw': 1,
  '00Yi9btvr8RY4NROm': 1,
  '00QLfPkuNx1jE7sMm': 1,
  mono: 0.4
};

const themeDictionary = {
  '00RnZfzWN7Ewml76I': 'Rest in dust',
  '00bYahDYU6E7wDJwL': 'Rustic',
  '00QLNIKFhjs5rJCjr': 'November',
  '00RB4IFxoIYII3Cqi': 'Velveteen',
  '014wmwCkvNXm2qCVW': 'Synthwave',
  '014cMCdTh3bwH8wVB': 'Party animal',
  '00bYcjDCylnf3k1aB': 'Marigolds',
  '014xtY7NR86Aa2AMH': 'Freeskate',
  '014c8uRpFlkiHSo9k': 'Japanese Gardens',
  '00bYcj4uvi0NIlqyw': 'Lost in the forest',
  '00Yi9btvr8RY4NROm': 'Aunt Dilemna',
  '00QLfPkuNx1jE7sMm': 'Chocolate shake',
  mono: 'Sinister'
};

const theme = weightedKey(themes, RND);

const features = {
  Zoom: weightedKey(zooms),
  Character: weightedKey(characters),
  Theme: themeDictionary[theme]
};

registerFeatures(features);
console.log('features', features);

export default function App() {
  /*
  useEffect(() => {
    console.log("AUTO STOP");
    clock.stop();
  }, [clock]);
  */

  const [isPlaying, setIsPlaying] = useState(true);

  useKeypress(' ', () => {
    setIsPlaying(!isPlaying);
  });

  return (
    <Canvas
      shadows={true}
      dpr={[1, 2]}
      frameloop={'demand'}
      camera={{
        position: [0, -15, features.Zoom === 'Far' ? 45 : 15],
        // zoom: 20,
        near: 0.1,
        far: 150
      }}
      // orthographic={true}
      gl={{
        preserveDrawingBuffer: true,
        autoClear: true,
        autoClearColor: 0x00000000
      }}
      onCreated={({ gl }) => {
        // gl.domElement.classList.add('hidden');
      }}
    >
      <OrbitControls dampingFactor={1} />
      <fog attach="fog" args={[0x000000, 18, 65]} />
      <directionalLight
        position={[-10, 20, 40]}
        intensity={1.5}
        castShadow
        shadow-mapSize={[1024, 1024]}
        shadow-shadowBias={0.001}
        shadow-camera-near={0.1}
        shadow-camera-far={50}
        shadow-camera-left={-20}
        shadow-camera-right={20}
        shadow-camera-top={20}
        shadow-camera-bottom={-20}
        lookAt={[0, 0, 0]}
      />
      {/* <pointLight position={[-5, 0, -100]} intensity={0.75} /> */}
      <ambientLight intensity={features.Theme === 'Sinister' ? 2.4 : 1.5} />
      <Model
        isPlaying={isPlaying}
        position={[0, -6, 0]}
        rotation={[Math.PI / 4, 0, Math.PI / 4]}
      />
      <ImageExporter />
    </Canvas>
  );
}

function Model(props) {
  const { invalidate } = useThree();

  useFrame(() => {
    if (props.isPlaying) invalidate();
  });

  const group = useRef();
  useFrame((state) => {
    group.current.rotation.x += 0.002;
    group.current.rotation.y = Math.sin(
      state.clock.elapsedTime * 0.024 * Math.PI * 2
    );
  });

  return (
    <group ref={group} {...props}>
      <Boxes i={100} j={100} isPlaying={props.isPlaying} />
    </group>
  );
}

const Boxes = ({ i, j, isPlaying }) => {
  // const [material] = useState(new THREE.MeshLambertMaterial());
  // const [boxesGeometry] = useState(
  //   new THREE.BoxGeometry(0.15, 1, 0.1, 1, 1, 1)
  // );
  const [frameIndex, setFrameIndex] = useState(0);
  const [noiseOffset] = useState(
    new THREE.Vector3(RND.float(1000), RND.float(1000), RND.float(1000))
  );

  const poline = useMemo(() => {
    // const theme = pick(themes, RND);
    console.log('theme', theme);
    let anchorColors;
    if (theme === 'mono') {
      anchorColors = [
        [0, 0, 0],
        [312, 0.051, 0.8]
      ];
    } else {
      anchorColors = THEMES[theme].map((c) => {
        const color = new THREE.Color(c);
        const hsl = {};
        color.getHSL(hsl);
        return [hsl.h * 360, hsl.s, hsl.l];
      });
    }

    // const s = RND.float() / 2 + 0.5;
    // const l = 0.56;
    // const h = RND.float() * 360;

    const p = new Poline({
      numPoints: 6,
      closedLoop: false,
      // anchorColors: [
      //   [76, 0.97, 0.80],
      //   [277, 0.62, 0.00],
      // ],
      anchorColors,
      positionFunctionX: positionFunctions.sinusoidalPosition,
      positionFunctionY: positionFunctions.quadraticPosition,
      positionFunctionZ: positionFunctions.linearPosition
    });

    for (var c in p.colorsCSS) {
      logColor(p.colorsCSS[c]);
    }

    // for (var c in p.colors) {
    //   logColor(new THREE.Color().fromArray(p.colors[c]).getStyle());
    // }

    return p;
  }, []);

  const colorArray = useMemo(() => {
    const colors = poline.colorsCSS;
    const color = new THREE.Color();
    const colorArray = Float32Array.from(
      new Array(i * j).fill().flatMap((_, k) => {
        const c = colors[k % colors.length];
        return color.set(c).toArray();
      })
    );
    // c1 = new THREE.Color(),
    // c2 = new THREE.Color();
    // c1 = new THREE.Color(Math.floor(RND.float() * colors.length)),
    // c2 = new THREE.Color(
    //   colors[1 + Math.floor(RND.float() * colors.length)]
    // );

    return colorArray;
  }, [i, j, poline.colorsCSS]);

  const material = useMaterial({
    style: 0
    // color: features.Character === 'Friendly' ? '#ffffff' : '#030303'
  });

  const ref = useRef();

  useFrame(({ clock }) => {
    if (!ref.current || !isPlaying) return;

    let counter = 0;
    const t = frameIndex * 0.013;
    // const t = clock.elapsedTime * 0.4;

    const tempBoxes = new THREE.Object3D();
    let p = new THREE.Vector3(),
      p2 = new THREE.Vector3(),
      p3 = new THREE.Vector3(),
      p4 = new THREE.Vector3(),
      c = new THREE.Color(),
      _c = new THREE.Color(),
      n = 0,
      n2 = 0,
      v = 0,
      oldN = 0,
      style;

    // if (RND.float() > .998) {
    //   setNewProfile(true)
    // }

    poline.shiftHue(features.Character === 'shifting' ? 0.4 : 0.01);

    const colorCSS = poline.colorsCSS;
    // const _colors = poline.colors;

    for (let x = 0; x < i; x++) {
      for (let z = 0; z < j; z++) {
        tempBoxes.matrix.identity();
        const id = counter++;
        p.set(i / 2 - x, j / 2 - z, 0);
        p.multiplyScalar(0.65);
        p2.copy(p).add(noiseOffset).multiplyScalar(0.031);
        p3.copy(p)
          .add(new THREE.Vector3(t, t + x, t + z))
          .multiplyScalar(0.0421);
        n = noise.simplex3(...p2.toArray());
        n2 = noise.simplex3(p3.x, p3.y, p3.z + p3.x);
        p4.copy(p)
          .add(new THREE.Vector3(t - 1, t - 1 + x, t - 1 + z))
          .multiplyScalar(0.0421);
        oldN = noise.simplex3(...p4.toArray());
        v = oldN - n;

        tempBoxes.position.set(
          p.x + id * 0.0001 + n2 * 13,
          p.y + n * 25,
          p.z + n2 * 25
        );

        // hsl(66, 20%, 80%)

        // if (id % 10 === -1) {
        //   c.setStyle(`hsl(0, 100%, 0%)`);
        // } else {
        style = colorCSS[id % colorCSS.length];
        // style = colorCSS[0];
        c.setStyle(style);

        _c.setStyle(`hsl(0, 80%, 80%)`);

        c.lerpHSL(_c, v * 0.15);
        // }

        // ref.current.setColorAt(id, n < -0.24 ? new THREE.Color(0x030303) : c);
        ref.current.setColorAt(id, c);

        // tempBoxes.scale.set(n * 10, n * 10, n * 10);
        let vm = Math.abs(v) > 0.25 ? (Math.abs(v) - 0.25) * 10 : 1;
        tempBoxes.scale.set(
          Math.max(0.35, Math.abs(p.x) * v),
          Math.max(0.1, Math.abs(p.y)),
          Math.max(0.35, vm)
        );

        // tempBoxes.scale.set(
        //   Math.abs(tempBoxes.position.z),
        //   Math.abs(tempBoxes.position.y),
        //   Math.abs(tempBoxes.position.x)
        // );

        // tempBoxes.rotation.x = n * Math.PI * 2;
        tempBoxes.rotation.x = n * Math.PI * 2;
        tempBoxes.rotation.z =
          n2 * 2.3 * Math.PI * (features.Character === 'Friendly' ? 0.4 : 2.4);

        tempBoxes.updateMatrix();
        ref.current.setMatrixAt(id, tempBoxes.matrix);
      }
    }
    // ref.current.geometry.attributes.colors.needsUpdate = true;
    ref.current.instanceMatrix.needsUpdate = true;
    ref.current.instanceColor.needsUpdate = true;

    if (frameIndex === 50) window.fxpreview();

    if (isPlaying) setFrameIndex(frameIndex + 1);
  });

  return (
    <instancedMesh
      ref={ref}
      args={[null, material, i * j]}
      receiveShadow={true}
      castShadow={true}
    >
      <boxGeometry args={[0.1, 0.1, 1, 1, 1, 1]}>
        <instancedBufferAttribute
          attach="attributes-color"
          args={[colorArray, 3]}
        />
      </boxGeometry>
      {/* <meshBasicMaterial toneMapped={false} vertexColors /> */}
    </instancedMesh>
  );
};

function ImageExporter() {
  const { gl } = useThree();

  const link = useMemo(() => {
    var link = document.createElement('a');
    document.body.appendChild(link);

    return link;
  }, []);

  useKeypress('s', () => {
    const strMime = 'image/jpeg',
      strDownloadMime = 'image/octet-stream',
      imgData = gl.domElement.toDataURL(strMime),
      strData = imgData.replace(strMime, strDownloadMime),
      filename = 'cubes.jpg';

    if (typeof link.download === 'string') {
      //Firefox requires the link to be in the body
      link.download = filename;
      link.href = strData;
      link.click();
      // document.body.removeChild(link); //remove the link when done
    }
  });

  return null;
}
